import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  ReactRequiredChildren,
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

import { useHead } from "./HeadContext";
import styles from "./ComparisonTable.scss";

export const variantValues = ["big", "small"];
export type VariantValue = typeof variantValues[number];

export const verticalAlignValues = [
  "top",
  "middle",
  "baseline",
  "bottom",
] as const;
export type VertcialAlignValue = typeof verticalAlignValues[number];

type ComparisonTableHeadColHeaderBaseProps = {
  id?: string;
  variant?: VariantValue;
  verticalAlign?: VertcialAlignValue;
};

export type ComparisonTableHeadColHeaderProps = ComparisonTableHeadColHeaderBaseProps &
  Omit<
    ReactRequiredChildren<React.ComponentPropsWithoutRef<"th">>,
    keyof ComparisonTableHeadColHeaderBaseProps
  >;

const defaultProps = {
  variant: "big",
  id: "",
  verticalAlign: "baseline",
} as const;

const validatedDefaultProps: ReactDefaultProps<ComparisonTableHeadColHeaderBaseProps> = defaultProps;

export const ComparisonTableHeadColHeader: ((
  props: ComparisonTableHeadColHeaderProps
) => JSX.Element) &
  ReactComponentStaticProps<ComparisonTableHeadColHeaderProps> = ({
  children,
  variant = defaultProps.variant,
  verticalAlign,
  id = defaultProps.id,
  ...restProps
}) => {
  const { alignment } = useHead();
  const variantStyle = `head-col-header--${variant}` as const;
  return (
    <th
      scope="col"
      className={cx(styles["head-col-header"], styles[variantStyle], {
        [styles["head-col-header--left-align"]]: alignment === "left",
      })}
      style={{ verticalAlign }}
      id={id}
      {...restProps}
    >
      <div
        className={cx({
          [styles["head-col-header--left-align-center"]]:
            alignment === "left-center",
          [styles["head-col-header--center-align-left"]]:
            alignment === "center-left",
        })}
      >
        {children}
      </div>
    </th>
  );
};

ComparisonTableHeadColHeader.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  variant: PropTypes.oneOf(variantValues),
  verticalAlign: PropTypes.oneOf(verticalAlignValues),
  id: PropTypes.string,
};

ComparisonTableHeadColHeader.defaultProps = validatedDefaultProps;

ComparisonTableHeadColHeader.displayName = "ComparisonTable.HeadColHeader";

export default ComparisonTableHeadColHeader;
